import { Component, Input, OnInit } from '@angular/core';
import { ColDef, ColumnResizedEvent, ColumnVisibleEvent, GridApi, GridOptions, GridReadyEvent, GridSizeChangedEvent } from 'ag-grid-community';
import { AgGridHelperService } from 'src/app/reusables/services/ag-grid-helper.service';
import { CvConfigurationService } from 'src/app/reusables/services/cv-configuration.service';
import { ModuleManager } from 'src/shared/models/module-manager';
import { CxonePlayIconCellComponent } from './cxone-play-icon-cell/cxone-play-icon-cell.component';
import { ChannelTypeCellRendererComponent } from './channel-type-cell-renderer/channel-type-cell-renderer.component';
import { channelTypeToIconMap, CxoneQualityEvaluationsField, DataRow } from './cxone-quality-evaluations.model';
import { IconDetail } from './channel-type-cell-renderer/channel-type-cell-renderer.model';
import { CVLocalizationService } from 'src/app/reusables/services/cv-localization.service';
import { CxoneQualityEvaluationsService } from './cxone-quality-evaluations.service';
import { IModuleManagerMenuItem, ModuleManagerMenuItemType } from 'src/shared/models/module-manager/module-manager-menu-item.model';

@Component({
    selector: 'cxone-quality-evaluations',
    templateUrl: './cxone-quality-evaluations.component.html',
    styleUrl: './cxone-quality-evaluations.component.scss'
})
export class CxoneQualityEvaluationsComponent implements OnInit {
    @Input() moduleManager: ModuleManager;
    @Input() moduleKey: string;
    public gridOptions: GridOptions;
    public gridApi: GridApi;
    public noData: boolean = false;
    public tableState = null;
    public params: any;
    private stateRestoreInProgress = false;
    public stateSaveTimer: number;
    public showFilter = false;
    public searchPlaceholder = "";
    public filterValue = '';
    public dataLoadSuccess = false;
    public isFirstLoad = true;
    public isDataLoading = false;
    public data: DataRow[] = [];
    public columnFields: CxoneQualityEvaluationsField[] = [];
    //To show/hide 10,000 rows limit notification
    public isLimitNotificationShown = false;
    public isLimitNotificationClosed = false;
    public limitNotificationMessage = "";
    public reportName = "";
 
    constructor(
        public configService: CvConfigurationService,
        private agGridHelperService: AgGridHelperService,
        private cvLocalizationService: CVLocalizationService,
        private qualityEvaluationsService: CxoneQualityEvaluationsService
    ) { }

    private get isResizeWaiting() { return this.moduleManager?.container.isResizeWaiting; };

    async ngOnInit() {
        const tasks = [
            this.cvLocalizationService.GetSection('CXOne_Quality_Evaluations_Module'),
            this.cvLocalizationService.GetSection('Error_Module'),
            this.cvLocalizationService.GetSection('Common'),
            this.cvLocalizationService.GetSection('Widgets'),
        ];
        await Promise.all(tasks);

        this.searchPlaceholder = this.cvLocalizationService.getCachedText("Common", "Search", "Search");
        this.limitNotificationMessage = this.cvLocalizationService.Format(
            this.cvLocalizationService.getCachedText("Widgets", "Limit_Notification_Message", "Displaying the latest {0} records. Adjust filters or export widget to see more."),
            "10,000"
        );

        this.moduleManager.setOptions({
            name: 'Quality Evaluations',
            enableWidthClass: false,
            widgetMode: false,
            onSettingsApplied: () => this.loadData(false),
            hasRefresh: true,
            refreshType: null,
            onRefresh: (isAutoRefresh: boolean) => this.loadData(isAutoRefresh),
            canRefresh: () => !this.isDataLoading,
            onExport: (format) => this.qualityEvaluationsService.LoadDataForExport(this.moduleKey, this.reportName, format),
        });

        this.setupGrid();

        this.moduleManager.addState({
            name: "tableState",
            getStateData: () => this.tableState,
            onStateLoaded: (stateData) => {
                if (!stateData) {
                    return;
                }

                this.tableState = stateData;
                this.restoreColumnState();
            },
            autoLoad: true
        });

        this.moduleManager.addHeaderItem({
            name: "Search",
            icon: "search",
            click: () => this.toggleOpenSearch(),
        });

        this.menuItemsForTable();
    }

    menuItemsForTable() {
        this.moduleManager.additionalMenuItems = null;
        this.moduleManager.addMenuItem({
            name: this.cvLocalizationService.getCachedText('CXOne_Quality_Evaluations_Module', 'Columns', 'Columns'),
            icon: "columns",
            getEnabled: () => this.gridApi && this.gridOptions.columnDefs.length > 0,
            type: ModuleManagerMenuItemType.CheckMenu,
            getCheckItems: () => this.gridOptions.columnDefs,
            getItemName: i => this.gridOptions.columnDefs[i].headerName,
            isItemChecked: i => {
                if (!this.tableState) {
                    this.prepareTableState();
                }
                const columnDefinition: ColDef = this.gridOptions.columnDefs[i];

                const tableStateMatch = this.tableState.columns.find(c => c.name === columnDefinition.field);
                if (tableStateMatch) {
                    columnDefinition.hide = !tableStateMatch.visible;
                }
                return !columnDefinition.hide;
            },
            onCheckClicked: i => this.toggleColumnVisible(i),
        } as IModuleManagerMenuItem);
    }

    toggleColumnVisible(i) {
        const columnDefinition: ColDef = this.gridOptions.columnDefs[i];
    
        if (columnDefinition) {
            columnDefinition.hide = !columnDefinition.hide;
            this.gridApi.setColumnVisible(columnDefinition.field, !columnDefinition.hide);
            this.prepareTableState();
            this.queueStateSave();
        }
    }
    
    prepareTableState() {
        if (!this.gridApi)
            return;

        const tableState = this.agGridHelperService.getGridState(this.gridApi);
        if (tableState !== null) {
            this.tableState = tableState;
        }
    }

    async loadData(isAutoRefresh: boolean) {
        if (this.isDataLoading && isAutoRefresh)
            return;
        this.isDataLoading = true;

        this.moduleManager.showLoader = !isAutoRefresh;
        const { reportingSetName } = this.moduleManager.moduleSettings;
        this.reportName = reportingSetName;
 
        try {
            const response = await this.qualityEvaluationsService.loadQualityEvaluationsData(this.moduleKey, this.reportName, isAutoRefresh, this.isFirstLoad);
            this.isFirstLoad = false;
            this.moduleManager.setAutoRefreshReady();
            this.data = response.data.DataRows;
 
            const defaultColumnProperties: Partial<CxoneQualityEvaluationsField> = {
                resizable: true,
                suppressMovable: false,
                filter: 'agTextColumnFilter',
            };

            this.columnFields = response.data.FieldDefinitions.filter((column: any) => column.FieldName !== 'CHANNEL_DESCRIPTION').map((column: any) =>  {
                let updatedColumn: Partial<CxoneQualityEvaluationsField> = {
                    ...defaultColumnProperties,
                    headerName: column.FieldDisplay.replace(/_/g, ' '),
                    field: column.FieldName,
                };
 
                if (column.FieldName === 'EVALUATED_SEGMENT_CHANNEL_TYPE') {  
                    updatedColumn = {
                        ...updatedColumn,
                        cellRenderer: 'channelTypeCellRenderer',
                        cellRendererParams: {
                            iconPath: (data) => this.getIconPath(data)
                        },
                        valueGetter: (params: any) => {
                            return params.data?.CHANNEL_DESCRIPTION || '';
                        }
                    } as Partial<CxoneQualityEvaluationsField>;
                }

                return updatedColumn as CxoneQualityEvaluationsField;
            });

            if (this.reportName === 'Evaluation Details') {
                this.columnFields.push({
                    headerName: this.cvLocalizationService.getCachedText("CXOne_Quality_Evaluations_Module", "Play", "Play"),
                    field: 'action',
                    resizable: false,
                    cellRenderer: 'cxonePlayIconCellComponent'
                } as CxoneQualityEvaluationsField);
            }

            this.gridOptions.columnDefs = this.columnFields;

            if (this.gridApi) {
                this.gridApi.setGridOption('rowData', this.data);
            }
        } catch (e) {
            this.moduleManager.handleLoadError(e, isAutoRefresh);
        } finally {
            this.applyFilter();
            this.isDataLoading = false;
            this.noData = !this.data || this.data.length === 0;
            this.dataLoadSuccess = !this.noData;
            this.moduleManager.showLoader = false;
        }

        this.moduleManager.moduleOptions.nameSuffix = ` (${this.data?.length || 0})`;

        this.showLimitNotification();
    }

    setupGrid() {
        this.gridOptions = this.agGridHelperService.createGridOptions({
            defaultColDef: {
                sortable: true,
                filter: true,
                lockPinned: false,
                resizable: true,
                suppressMenu: false,
                enableValue: true,
                minWidth: this.agGridHelperService.defaultMinWidth,
            },
            columnDefs: [],
            rowData: [],
            accentedSort: true,
            animateRows: true,
            suppressCellFocus: true,
            suppressScrollOnNewData: true,
            suppressContextMenu: false,
            suppressHorizontalScroll: false,
            components: {
                channelTypeCellRenderer: ChannelTypeCellRendererComponent,
                cxonePlayIconCellComponent: CxonePlayIconCellComponent
            },
            onGridReady: (event: GridReadyEvent) => this.onGridReady(event),
            onGridSizeChanged: this.onGridSizeChanged.bind(this),
            onColumnVisible: this.onColumnVisible.bind(this),
            onFilterChanged: () => this.updateTableState(),
            onSortChanged: () => this.updateTableState(),
            onColumnMoved: () => this.updateTableState(),
            onColumnPinned: () => this.updateTableState(),
            onColumnResized: this.onColumnResized.bind(this),
            onCellClicked: (params: any) => this.params = params,
            overlayNoRowsTemplate: `<div class="error-container" style="padding-top: 48px;">
                                        <svg class="mb-2">
                                            <use href="../App_Themes/CXone/icons/no-results.svg#no-results"></use>
                                        </svg>
                                        <p class="cxone-error-text">
                                            ${this.cvLocalizationService.getCachedText("Error_Module", "CXOne_No_Results", "No results found")}
                                        </p>
                                    </div>`,
            suppressDragLeaveHidesColumns: true,
            suppressNoRowsOverlay: true,
        });
    }

    onGridReady(event: GridReadyEvent) {
        this.gridApi = event.api;
        if (this.gridApi) {
            this.gridApi.setGridOption('columnDefs', this.columnFields);
            this.gridApi.setGridOption('rowData', this.data);
        }

        this.stateRestoreInProgress = true;

        this.restoreColumnState();
        if (!this.tableState) {
            this.agGridHelperService.sizeColumnsSmart(event);
            this.updateTableState();
        }

        setTimeout(() => this.stateRestoreInProgress = false, 0);
    }
 
    public getIconPath(data): IconDetail {
        const iconType = channelTypeToIconMap[typeof data['EVALUATED_SEGMENT_CHANNEL_TYPE'] === 'string' ? data['EVALUATED_SEGMENT_CHANNEL_TYPE'] : ''];
        return { iconType };
    }

    onColumnResized(event: ColumnResizedEvent) {
        if (event.source === "uiColumnResized" || (event.source == "autosizeColumns" && !this.moduleManager.container.isTogglingFullscreen)) {
            this.updateTableState();
        }
    }

    onColumnVisible(event: ColumnVisibleEvent) {
        if (this.tableState === null || this.tableState.columns.map(c => c.width).filter(w => w !== null).length === 0)
            this.agGridHelperService.sizeColumnsSmart(event);
        this.updateTableState();
    }

    onGridSizeChanged(event: GridSizeChangedEvent) {
        if (this.isResizeWaiting) {
            this.agGridHelperService.sizeColumnsSmart(event);
            this.queueStateSave();
        } else if (this.moduleManager.container.isFullscreen) {
            this.agGridHelperService.sizeColumnsSmart(event);
        } else {
            this.restoreColumnState();
        }
    }

    queueStateSave() {
        if (this.stateSaveTimer !== null) {
            clearTimeout(this.stateSaveTimer);
        }

        this.stateSaveTimer = window.setTimeout(async () => {
            this.stateSaveTimer = null;
            if (await this.moduleManager.saveState('tableState')) {
                this.moduleManager.requestPublishNeeded();
            }
        }, 500);
    }

    updateTableState() {
        if (!this.gridApi || this.stateRestoreInProgress)
            return;

        const tableState = this.agGridHelperService.getGridState(this.gridApi);
        if (tableState !== null) {
            this.tableState = tableState;
        }

        this.queueStateSave();
    }

    restoreColumnState() {
        if (!this.tableState || !this.gridApi) {
            return;
        }

        this.agGridHelperService.loadGridState(this.gridApi, this.tableState);
    }

    public toggleOpenSearch() {
        if (this.showFilter) {
            this.showFilter = false;
            this.gridApi?.setGridOption('suppressNoRowsOverlay', true);
            this.clearFilter();
        } else {
            this.showFilter = true;
            this.gridApi?.setGridOption('suppressNoRowsOverlay', false);
            this.focusOnSearch();
        }
    }

    async focusOnSearch() {
        setTimeout(() => {
            const el = document.querySelector(`div.cxone-text-input input[name='cxoneInput-${this.moduleKey}']`);
            $(el).focus();
        }, 200);
    }

    public applyFilter() {
        if (this.gridApi) { 
            this.gridApi.setGridOption('quickFilterText', this.filterValue);
            if (this.showFilter && this.gridApi?.getDisplayedRowCount() === 0 && this.filterValue.length !== 0)
                this.gridApi?.showNoRowsOverlay();
            else
                this.gridApi?.hideOverlay();
        }
    }

    public clearFilter() {
        this.filterValue = "";
        this.applyFilter();
    }

    showLimitNotification() {
        if (!this.isLimitNotificationClosed) {
            this.isLimitNotificationShown = this.data.length >= 10000;
        }
    }

    closeLimitNotification() {
        this.isLimitNotificationShown = false;
        this.isLimitNotificationClosed = true;
    }
}
